import React from 'react';
import Navigation from './Navigation';
import { useAllTransactions } from '../hooks/useAllTransactions';
import { toMMDDYYYY } from '../utils/dateFormat';
import { formatCurrency } from '../utils/formatCurrency';
import '../styles/analytics.css';

function Analytics({ onLogout, onNavigate, userName, people: propsPeople }) {
  const people = propsPeople || [];
  const allTransactions = useAllTransactions(people);
  
  // Calculate statistics
  const totalPeople = people.length;
  
  const allStores = people.flatMap(p => p.stores || []);
  
  // Sadece email adresi olan store'ları say (aktif olanlar)
  const activeStores = allStores.filter(s => s.email && s.email.trim() !== '');
  const totalStores = activeStores.length;
  
  const storesTotal = allStores.reduce((sum, s) => sum + (s.bal || 0), 0);
  const expensesTotal = people.reduce((sum, p) => sum + (p.expenses || []).reduce((s, e) => s + (e.amount || 0), 0), 0);
  const totalBalance = storesTotal - expensesTotal;
  
  const totalIncome = allTransactions
    .filter(t => t.type === 'income')
    .reduce((sum, t) => sum + t.amount, 0);
  
  // Store-level expenses (from transactions) + Person-level expenses (from balance modal)
  const storeExpenses = allTransactions
    .filter(t => t.type === 'expense')
    .reduce((sum, t) => sum + t.amount, 0);
  const personExpensesTotal = people.reduce((sum, p) => sum + (p.expenses || []).reduce((s, e) => s + (e.amount || 0), 0), 0);
  const totalExpense = storeExpenses + personExpensesTotal;
  
  const netProfit = totalIncome - totalExpense;
  
  // Recent transactions (last 10)
  const recentTransactions = allTransactions.slice(0, 10);

  // All person-level expenses - en son eklenen en üstte (createdAt)
  const allExpenses = people.flatMap(p => 
    (p.expenses || []).map(e => ({
      ...e,
      personName: p.name,
      personCode: p.code
    }))
  ).sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));

  return (
    <div>
      <Navigation 
        currentPage="analytics" 
        onNavigate={onNavigate}
        onLogout={onLogout}
        userName={userName}
      />
      
      <div className="container">
        <h2 className="analytics-title">📊 Analytics Dashboard</h2>

        {/* Stats Cards */}
        <div className="analytics-stats">
          <div className="stat-card primary">
            <div className="stat-icon">👥</div>
            <div className="stat-content">
              <div className="stat-value">{totalPeople}</div>
              <div className="stat-label">Total People</div>
            </div>
          </div>

          <div className="stat-card secondary">
            <div className="stat-icon">🏪</div>
            <div className="stat-content">
              <div className="stat-value">{totalStores}</div>
              <div className="stat-label">Active Stores</div>
            </div>
          </div>

          <div className="stat-card success">
            <div className="stat-icon">💰</div>
            <div className="stat-content">
              <div className="stat-value">${totalBalance.toLocaleString()}</div>
              <div className="stat-label">Total Balance</div>
            </div>
          </div>

          <div className="stat-card info">
            <div className="stat-icon">➕</div>
            <div className="stat-content">
              <div className="stat-value">${totalIncome.toLocaleString()}</div>
              <div className="stat-label">Total Income</div>
            </div>
          </div>

          <div className="stat-card danger">
            <div className="stat-icon">➖</div>
            <div className="stat-content">
              <div className="stat-value">${totalExpense.toLocaleString()}</div>
              <div className="stat-label">Total Expense</div>
            </div>
          </div>

          <div className="stat-card warning">
            <div className="stat-icon">📈</div>
            <div className="stat-content">
              <div className="stat-value">${netProfit.toLocaleString()}</div>
              <div className="stat-label">Net Profit</div>
            </div>
          </div>
        </div>

        {/* Expenses (Person-level - from balance modal) */}
        <div className="analytics-section">
          <h3 className="section-title">➖ Expenses</h3>
          {allExpenses.length > 0 ? (
            <div className="recent-transactions-list">
              {allExpenses.map(e => (
                <div key={e.id} className="recent-transaction-item expense-item">
                  <div className="transaction-type-icon">➖</div>
                  <div className="transaction-info">
                    <div className="transaction-desc-main">{e.description || 'Expense'}</div>
                    <div className="transaction-meta">
                      {e.personName} (#{e.personCode}) • {toMMDDYYYY(e.date) || e.date}
                    </div>
                  </div>
                  <div className="transaction-amount-main expense">
                    -${Math.abs(e.amount || 0).toFixed(2)}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <div className="empty-icon">📋</div>
              <div>No expenses yet. Click on balance in People page to add.</div>
            </div>
          )}
        </div>

        {/* Recent Transactions */}
        <div className="analytics-section">
          <h3 className="section-title">📋 Recent Transactions</h3>
          {recentTransactions.length > 0 ? (
            <div className="recent-transactions-list">
              {recentTransactions.map(t => (
                <div key={t.id} className="recent-transaction-item">
                  <div className="transaction-type-icon">
                    {t.type === 'income' ? '➕' : '➖'}
                  </div>
                  <div className="transaction-info">
                    <div className="transaction-desc-main">{t.desc || t.description}</div>
                    <div className="transaction-meta">
                      {t.storeName || t.storeCode} • {toMMDDYYYY(t.date) || t.date}
                    </div>
                  </div>
                  <div className={`transaction-amount-main ${t.type}`}>
                    {t.type === 'income' ? '+' : '-'}${formatCurrency(Math.abs(t.amount))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <div className="empty-icon">📊</div>
              <div>No transactions yet</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Analytics;